import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { DealerManagementService } from '../../../services/dealer-management.service';
import { DealerManagementTableData } from 'src/services/models/dealer-management.service';
import DialogModal from '../../ui/dialogModal';
import Dropdown from '../../ui/dropdownMenuTable/dropdownMenuTable';
import { HttpStatusCode } from '../../../utils/enums/http-method.enum';
import OptionsIcon from 'src/components/dealer-mgmt/options-icon';

import { useLocalization } from 'src/utils/useLocalization';
import { useState } from 'react';
import './style.scss';

export interface ITableContents {
    items: DealerManagementTableData[];
    onClick: (name: string) => void;
    updateData: () => void;
    setIsSuccessDeactivate: (open: boolean) => void;
    setIsSuccessActivate: (open: boolean) => void;
    setIsSuccessDeleted: (open: boolean) => void;
    setIsSuccessDeletedMFA: (open: boolean) => void;
    setIsError: (open: boolean) => void;
}

export interface Props {
    data: DealerManagementTableData;
    onClick: (userId: string) => void;
    updateData: () => void;
    setIsSuccessDeactivate: (open: boolean) => void;
    setIsSuccessActivate: (open: boolean) => void;
    setIsSuccessDeleted: (open: boolean) => void;
    setIsSuccessDeletedMFA: (open: boolean) => void;
    setIsError: (open: boolean) => void;
}

const MenuItem = ({
    userId,
    status,
    editHandler,
    userName,
    deactivateHandler,
    activateHandler,
    deleteUserHandler,
    deleteMFAHandler,
}: {
    userId: string;
    status: string;
    editHandler: Function;
    userName: string;
    deactivateHandler: Function;
    activateHandler: Function;
    deleteUserHandler: Function;
    deleteMFAHandler: Function;
}) => {
    const { dealerMgmt } = useLocalization();
    return (
        <>
            <li className='menu-items' onClick={() => editHandler(userName)}>
                <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_EDIT}</span>
            </li>
            <li
                className='menu-items'
                onClick={() => deleteMFAHandler(userName)}
            >
                <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_DELETEMFA}</span>
            </li>
            {status === 'Suspended' && (
                <li
                    className='menu-items'
                    onClick={() => activateHandler(userId)}
                    data-testid={`table-item-activate-${userId}`}
                >
                    <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_ACTIVATE}</span>
                </li>
            )}
            {status === 'Active' && (
                <li
                    className='menu-items'
                    onClick={() => deactivateHandler(userId)}
                    data-testid={`table-item-deactivate-${userId}`}
                >
                    <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_DEACTIVATE}</span>
                </li>
            )}
            <li
                className='menu-items'
                onClick={() => {
                    deleteUserHandler();
                }}
            >
                <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_DELETE}</span>
            </li>
        </>
    );
};

export const TableItem = (props: Props) => {
    const [open, setOpen] = useState(false);
    const { userId, name, email, isAdmin, status, dealers } = props.data;
    // eslint-disable-next-line no-magic-numbers
    const [rowUserId, setRowUserId] = useState<string>('');
    const [modalOpen, setModalOpen] = useState<string>('');
    const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
    const { dealerMgmt, addNewUser } = useLocalization();

    const DEACTIVATED_STATUS = 1;
    const ACTIVATED_STATUS = 0;

    enum UserModalStatus {
        DELETE = 'DELETE',
        ACTIVATE = 'ACTIVATE',
        DEACTIVATE = 'DEACTIVATE',
        DELETEMFA = 'DELETEMFA',
    }

    const handleDeleteUserModal = () => {
        setModalOpen(UserModalStatus.DELETE);
    };
    const handleDeleteMFAModalOpen = () => {
        setModalOpen(UserModalStatus.DELETEMFA);
    };
    const activateDeactivateCall = async (status: number) => {
        const dealerManagementService = new DealerManagementService();
        try {
            return await dealerManagementService.updateUserStatus(
                rowUserId,
                status,
            );
        } catch (error) {
            console.error(error);
        }
    };
    const deleteUserCall = async () => {
        const dealerManagementService = new DealerManagementService();
        try {
            const response = await dealerManagementService.deleteUser(
                userId,
                email,
            );
            if (response.status !== HttpStatusCode.SUCCESS) {
                throw new Error('Error deleting the user');
            }
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteMFACall = async () => {
        const dealerManagementService = new DealerManagementService();
        try {
            const response = await dealerManagementService.resetMfa(email);
            if (response.status !== HttpStatusCode.SUCCESS) {
                throw new Error('Error deleting the mfa');
            }
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleDeactivateModalOpen = (id: string) => {
        setRowUserId(id);
        setModalOpen(UserModalStatus.DEACTIVATE);
    };

    const handleActivateModalOpen = (id: string) => {
        setRowUserId(id);
        setModalOpen(UserModalStatus.ACTIVATE);
    };

    const handleModalCancel = () => {
        setModalOpen('');
    };

    const deactivateModalConfirm = () => {
        setModalIsLoading(true);
        activateDeactivateCall(DEACTIVATED_STATUS)
            .then(() => {
                props.setIsSuccessDeactivate(true);
                setModalOpen('');
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const activateModalConfirm = () => {
        setModalIsLoading(true);
        activateDeactivateCall(ACTIVATED_STATUS)
            .then(() => {
                props.setIsSuccessActivate(true);
                setModalOpen('');
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const deleteUserConfirm = () => {
        setModalIsLoading(true);
        deleteUserCall()
            .then(() => {
                props.setIsSuccessDeleted(true);
                setModalOpen('');
                setModalIsLoading(false);
                props.updateData();
            })
            .catch(() => {
                props.setIsError(true);
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const deleteMFAConfirm = () => {
        setModalIsLoading(true);
        deleteMFACall()
            .then(() => {
                props.setIsSuccessDeletedMFA(true);
                setModalOpen('');
                setModalIsLoading(false);
                props.updateData();
            })
            .catch(() => {
                props.setIsError(true);
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const getTitleContent = () => {
        let label;
        switch (modalOpen) {
            case UserModalStatus.DELETE: {
                label = `${dealerMgmt.DEALER_MGMT_DELETE_MODAL_TITLE} ${name}`;
                break;
            }
            case UserModalStatus.ACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_TABLE_MENU_ACTIVATE;
                break;
            }
            case UserModalStatus.DEACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_DEACTIVATE_MODAL_TITLE;
                break;
            }
            case UserModalStatus.DELETEMFA: {
                label = dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_TITLE;
                break;
            }
            default: {
                label = '';
                break;
            }
        }
        return label;
    };
    const getBodyContent = () => {
        let label;
        switch (modalOpen) {
            case UserModalStatus.DELETE: {
                label = dealerMgmt.DEALER_MGMT_DELETE_MODAL_DESCRIPTION;
                break;
            }
            case UserModalStatus.ACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_ACTIVATE_MODAL_DESCRIPTION;
                break;
            }
            case UserModalStatus.DEACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_DEACTIVATE_MODAL_DESCRIPTION;
                break;
            }
            case UserModalStatus.DELETEMFA: {
                label = dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_DESCRIPTION;
                break;
            }
            default: {
                label = '';
                break;
            }
        }
        return label;
    };
    const getConfirmBtnLabel = () => {
        let label;
        switch (modalOpen) {
            case UserModalStatus.DELETE: {
                label = dealerMgmt.DEALER_MGMT_DELETE_MODAL_CONFIRM_BUTTON;
                break;
            }
            case UserModalStatus.ACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_ACTIVATE_MODAL_CONFIRM_BUTTON;
                break;
            }
            case UserModalStatus.DEACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_DEACTIVATE_MODAL_CONFIRM_BUTTON;
                break;
            }
            case UserModalStatus.DELETEMFA: {
                label = dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_CONFIRM_BUTTON;
                break;
            }
            default: {
                label = '';
                break;
            }
        }
        return label;
    };
    return (
        <>
            <DialogModal
                titleContent={getTitleContent()}
                bodyContent={getBodyContent()}
                open={modalOpen !== ''}
                cancelBtnLabel={addNewUser.MODAL_LABEL_BUTTON_CANCEL}
                confirmBtnLabel={getConfirmBtnLabel()}
                onCancelHandler={handleModalCancel}
                onConfirmHandler={() => {
                    modalOpen === UserModalStatus.DELETE
                        ? deleteUserConfirm()
                        : modalOpen === UserModalStatus.ACTIVATE
                        ? activateModalConfirm()
                        : modalOpen === UserModalStatus.DEACTIVATE
                        ? deactivateModalConfirm()
                        : modalOpen === UserModalStatus.DELETEMFA
                        ? deleteMFAConfirm()
                        : null;
                }}
                isLoading={modalIsLoading}
                isConfirmBtnRed={modalOpen === UserModalStatus.DELETE}
            />
            <div className='sl-table-row'>
                <div className='table-row col-1'>
                    <span className='list-label'>Name</span>
                    <span>{name}</span>
                </div>
                <div className='table-row col-2'>
                    <span className='list-label'>Email</span>
                    <span>{email}</span>
                </div>
                <div className='table-row col-3'>
                    <span className='list-label'>Admin</span>
                    {isAdmin ? (
                        <span className='c-badge__wrapper  '>
                            <span className='c-badge badge'>
                                {isAdmin ? 'Admin' : ''}
                            </span>
                        </span>
                    ) : (
                        <></>
                    )}
                </div>
                <div className='table-row col-4'>
                    <span className='list-label'>Status</span>
                    <span>
                        {status !== 'Suspended'
                            ? dealerMgmt.DEALER_MGMT_TABLE_USER_STATUS_ACTIVE
                            : dealerMgmt.DEALER_MGMT_TABLE_USER_STATUS_INACTIVE}
                    </span>
                </div>
                <div className='table-row col-5'>
                    <span className='list-label'>Dealerships</span>
                    <span>{dealers}</span>
                </div>
                <div className='table-row col-6'>
                    <span className='list-label'></span>
                    <span
                        className='options-icon'
                        data-testid={`table-item-${props.data.userId}`}
                        onClick={() => {
                            setOpen(true);
                        }}
                    >
                        <OptionsIcon />
                    </span>
                    <Dropdown
                        dropdown={open}
                        showSelected={false}
                        showSelectedIcon={false}
                        handlerChange={(value: boolean) => {
                            setOpen(value);
                        }}
                    >
                        <MenuItem
                            userId={userId}
                            status={status}
                            editHandler={props.onClick}
                            userName={email}
                            deleteUserHandler={handleDeleteUserModal}
                            deactivateHandler={handleDeactivateModalOpen}
                            activateHandler={handleActivateModalOpen}
                            deleteMFAHandler={handleDeleteMFAModalOpen}
                        />
                    </Dropdown>
                </div>
            </div>
        </>
    );
};

export const Table = (props: ITableContents) => {
    const { dealerMgmt } = useLocalization();
    const chevronColor = '#363b49';
    return (
        <div className='sl-table'>
            <div className='sl-table-row header-row hidden-in-list'>
                <div className='table-header col-1'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_NAME}
                    </span>
                    <span>
                        <Chevron
                            className='accordion__icon rotate-down'
                            width={10}
                            fill={chevronColor}
                        />
                    </span>
                </div>
                <div className='table-header col-2'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_EMAIL}
                    </span>
                </div>
                <div className='table-header col-3'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_ADMIN}
                    </span>
                </div>
                <div className='table-header col-4'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_STATUS}
                    </span>
                </div>
                <div className='table-header col-5'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_DEALERS}
                    </span>
                </div>
                <div className='table-header col-6 menu-header'></div>
            </div>
            {props.items.map((item, index) => (
                <TableItem
                    key={index}
                    data={item}
                    onClick={props.onClick}
                    updateData={props.updateData}
                    setIsSuccessDeleted={props.setIsSuccessDeleted}
                    setIsSuccessDeactivate={props.setIsSuccessDeactivate}
                    setIsSuccessActivate={props.setIsSuccessActivate}
                    setIsSuccessDeletedMFA={props.setIsSuccessDeletedMFA}
                    setIsError={props.setIsError}
                />
            ))}
        </div>
    );
};

export default Table;
